import { clamp, damp, isInteractiveTarget } from "../core/math.js";

export class CursorController {
  #element;
  #dialogElement;
  #store;
  #enabled;
  #target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  #current = { ...this.#target };
  #last = { ...this.#target, time: performance.now() };
  #activeCard = null;
  #activeButton = null;
  #activeDetailMedia = null;
  #animalActive = false;
  #cleanup = [];

  constructor({ element, dialogElement, store, reduced }) {
    this.#element = element;
    this.#dialogElement = dialogElement;
    this.#store = store;
    this.#enabled =
      !reduced && window.matchMedia("(pointer: fine)").matches;
  }

  init() {
    if (!this.#enabled) {
      return false;
    }
    document.body.classList.add("has-custom-cursor");
    document.body.dataset.cursor = "default";

    const moveHandler = (event) => this.#onMove(event);
    const leaveHandler = () => {
      this.#element.style.opacity = "0";
      this.#dialogElement.style.opacity = "0";
      this.#clearInteractiveTransforms();
    };
    const enterHandler = () => {
      this.#element.style.opacity = "1";
      this.#dialogElement.style.opacity = "1";
    };
    const downHandler = () => this.#createRipple();

    window.addEventListener("pointermove", moveHandler, { passive: true });
    document.documentElement.addEventListener("mouseleave", leaveHandler);
    document.documentElement.addEventListener("mouseenter", enterHandler);
    window.addEventListener("pointerdown", downHandler, { passive: true });
    this.#cleanup.push(
      () => window.removeEventListener("pointermove", moveHandler),
      () =>
        document.documentElement.removeEventListener(
          "mouseleave",
          leaveHandler
        ),
      () =>
        document.documentElement.removeEventListener(
          "mouseenter",
          enterHandler
        ),
      () => window.removeEventListener("pointerdown", downHandler)
    );
    return true;
  }

  #onMove(event) {
    const now = performance.now();
    const elapsed = Math.max(16, now - this.#last.time);
    const distance = Math.hypot(
      event.clientX - this.#last.x,
      event.clientY - this.#last.y
    );
    const velocity = (distance / elapsed) * 1000;
    this.#target.x = event.clientX;
    this.#target.y = event.clientY;
    this.#last = { x: event.clientX, y: event.clientY, time: now };

    const target = event.target;
    const materialTarget =
      target instanceof Element ? target.closest("[data-material]") : null;
    let material = materialTarget?.dataset.material ?? "default";
    if (
      target instanceof Element &&
      target.closest("p, h1, h2, h3, li") &&
      !isInteractiveTarget(target)
    ) {
      material = "text";
    } else if (isInteractiveTarget(target)) {
      material = "interactive";
    }
    if (this.#animalActive) {
      material = "animal";
    } else if (material === "birch") {
      material = "mist";
    }
    document.body.dataset.cursor = material;
    this.#store.patch(
      {
        pointer: {
          x: event.clientX,
          y: event.clientY,
          velocity,
          material
        }
      },
      "pointer:move"
    );
    this.#updateCard(target, event.clientX, event.clientY);
    this.#updateMagnet(target, event.clientX, event.clientY);
    this.#updateDetailMedia(target, event.clientX, event.clientY);
  }

  #updateCard(target, x, y) {
    const card =
      target instanceof Element ? target.closest(".project-card") : null;
    if (card !== this.#activeCard) {
      this.#activeCard?.style.removeProperty("--tilt-x");
      this.#activeCard?.style.removeProperty("--tilt-y");
      this.#activeCard = card;
    }
    if (!card || window.innerWidth < 768) {
      return;
    }
    const rect = card.getBoundingClientRect();
    const normalizedX = clamp(-1, (x - rect.left) / rect.width * 2 - 1, 1);
    const normalizedY = clamp(-1, (y - rect.top) / rect.height * 2 - 1, 1);
    card.style.setProperty("--tilt-y", `${normalizedX * 4}deg`);
    card.style.setProperty("--tilt-x", `${normalizedY * -4}deg`);
    card.style.setProperty("--glow-x", `${((x - rect.left) / rect.width) * 100}%`);
    card.style.setProperty("--glow-y", `${((y - rect.top) / rect.height) * 100}%`);
  }

  #updateMagnet(target, x, y) {
    const button =
      target instanceof Element
        ? target.closest(".wood-button, .copy-button")
        : null;
    if (button !== this.#activeButton) {
      if (this.#activeButton) {
        this.#activeButton.style.translate = "";
      }
      this.#activeButton = button;
    }
    if (!button) {
      return;
    }
    const rect = button.getBoundingClientRect();
    const offsetX = clamp(-3, (x - (rect.left + rect.width / 2)) * 0.08, 3);
    const offsetY = clamp(-3, (y - (rect.top + rect.height / 2)) * 0.08, 3);
    button.style.translate = `${offsetX}px ${offsetY}px`;
  }

  #updateDetailMedia(target, x, y) {
    const media =
      target instanceof Element
        ? target.closest(".project-detail__media")
        : null;
    if (media !== this.#activeDetailMedia) {
      if (this.#activeDetailMedia) {
        this.#activeDetailMedia.style.removeProperty("--detail-media-x");
        this.#activeDetailMedia.style.removeProperty("--detail-media-y");
      }
      this.#activeDetailMedia = media;
    }
    if (!media) {
      return;
    }
    const rect = media.getBoundingClientRect();
    const offsetX = clamp(
      -6,
      ((x - rect.left) / rect.width - 0.5) * -12,
      6
    );
    const offsetY = clamp(
      -4,
      ((y - rect.top) / rect.height - 0.5) * -8,
      4
    );
    media.style.setProperty("--detail-media-x", `${offsetX}px`);
    media.style.setProperty("--detail-media-y", `${offsetY}px`);
  }

  #clearInteractiveTransforms() {
    if (this.#activeCard) {
      this.#activeCard.style.removeProperty("--tilt-x");
      this.#activeCard.style.removeProperty("--tilt-y");
    }
    if (this.#activeButton) {
      this.#activeButton.style.translate = "";
    }
    if (this.#activeDetailMedia) {
      this.#activeDetailMedia.style.removeProperty("--detail-media-x");
      this.#activeDetailMedia.style.removeProperty("--detail-media-y");
    }
  }

  #createRipple() {
    const ripple = document.createElement("span");
    ripple.className = "pointer-ripple";
    ripple.style.left = `${this.#target.x}px`;
    ripple.style.top = `${this.#target.y}px`;
    const openDialog = document.querySelector("#project-dialog[open]");
    (openDialog ?? document.body).append(ripple);
    ripple.addEventListener("animationend", () => ripple.remove(), {
      once: true
    });
  }

  setAnimalActive(value) {
    this.#animalActive = value;
    if (value) {
      document.body.dataset.cursor = "animal";
    }
  }

  update(deltaSeconds) {
    if (!this.#enabled) {
      return this.#target;
    }
    this.#current.x = damp(
      this.#current.x,
      this.#target.x,
      18,
      deltaSeconds
    );
    this.#current.y = damp(
      this.#current.y,
      this.#target.y,
      18,
      deltaSeconds
    );
    const transform = `translate3d(${this.#current.x}px, ${this.#current.y}px, 0)`;
    this.#element.style.transform = transform;
    const dialog = this.#dialogElement.closest("dialog");
    if (dialog?.open) {
      const rect = dialog.getBoundingClientRect();
      const localX = this.#current.x - rect.left + dialog.scrollLeft;
      const localY = this.#current.y - rect.top + dialog.scrollTop;
      this.#dialogElement.style.transform =
        `translate3d(${localX}px, ${localY}px, 0)`;
    }
    return this.#target;
  }

  destroy() {
    this.#cleanup.forEach((cleanup) => cleanup());
    this.#clearInteractiveTransforms();
    document.body.classList.remove("has-custom-cursor");
    delete document.body.dataset.cursor;
  }
}
